import { Slide } from 'common/Carousel'
import { useOnVisibilityChange } from 'hooks/useOnVisibilityChange'
import { useStore } from 'lib/store'
import { ComponentProps } from 'lib/theme'
import { ControlType, NSFW_EVENT } from 'lib/tracking/types'
import { observer } from 'mobx-react-lite'
import { FeedPostVideoAsset } from 'models/FeedPostVideoAsset'
import { FeedPostAsset } from 'models/Post'
import { useContext, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import { FeedItemContext } from 'shared/FeedItem'
import { ImageMediaTile } from './ImageMediaTile'
import { VideoMediaTile } from './VideoMediaTile'

export interface AssetSlideProps extends ComponentProps<typeof Slide> {
  asset: FeedPostAsset
  alt?: string
  height?: number
  fullMode?: number
  isActive?: boolean
}

const DEF_HEIGHT = '100%'

export const _AssetSlide = ({ asset, height, isActive = false, onClick, css }: AssetSlideProps) => {
  const store = useStore()
  const slideRef = useRef<HTMLDivElement>(null)
  const { onContentLoaded } = useContext(FeedItemContext)

  const post = asset.parentPost

  const [assetBroken, setAssetBroken] = useState(false)

  const { documentHidden } = useOnVisibilityChange()

  const resolveVideoState = () => {
    switch (true) {
      case assetBroken:
        return 'error'
      case !isActive:
        return 'unmounted'
      case !documentHidden && isActive && post.isIntersecting && !store.ui.videoModalAsset:
        return 'playing'
      default:
        return 'paused'
    }
  }

  const handleAssetError = (err: any) => {
    switch (err?.name) {
      case 'AbortError':
        // AbortErrors may be triggered from unmounting the video, we can ignore these
        return
      case 'NotAllowedError':
        // Video must be muted to autoplay on mount on browsers, this can be avoided if we
        // move mobile to a PWA
        return store.ui.toggleMuteMedia()
      default:
        console.error(`asset error (${asset.media.url}): `, err)
        setAssetBroken(true)

        // Because the error placeholders are rendered after an async req, we still need to measure the element as it's considered dynamic.
        // The VideoMediaTile and ImageMediaTile dismount the error event target element (video/img) and render an SVG in place.
        // This means we cannot use the err.target element to pass to onContentLoaded to measure the parent row, and instead keep a ref of the slide container.
        if (slideRef.current) {
          onContentLoaded(slideRef.current)
        }
    }
  }

  return (
    <Slide
      onClick={onClick}
      ref={slideRef}
      className="slide"
      css={{
        backgroundColor: 'black',
        cursor: 'pointer',
        ...(height ? { paddingBottom: `${assetBroken ? 0 : height}%` } : { height: DEF_HEIGHT }),
        ...css
      }}
    >
      {asset instanceof FeedPostVideoAsset ? (
        <VideoMediaTile
          initDuration={asset.lastDuration}
          media={asset.media}
          tileHeight={height ? `${asset.heightPercentOfWidth}%` : DEF_HEIGHT}
          muted={store.ui.muteMedia}
          isVisible={post.isVisible}
          state={resolveVideoState()}
          isMobile={store.ui.isMobile}
          isPostModalOpen={store.ui.showPostModal}
          onError={handleAssetError}
          onProgress={(state) => {
            asset.setLastDuration(state.playedSeconds)
          }}
          onMute={() => {
            store.ui.toggleMuteMedia()
            store.analytics.track(NSFW_EVENT.VIDEO_CONTROLS, {
              feedType: store.feed.currentFeedType,
              postId: asset.parentPost.postId,
              controlType: store.ui.muteMedia ? ControlType.MUTE : ControlType.UNMUTE
            })
          }}
          onStart={() => {
            store.analytics.track(NSFW_EVENT.PLAY_VIDEO, {
              feedType: store.feed.currentFeedType,
              postId: asset.parentPost.postId
            })
          }}
          onPause={() => {
            store.analytics.track(NSFW_EVENT.PLAY_VIDEO_DURATION, {
              feedType: store.feed.currentFeedType,
              postId: asset.parentPost.postId,
              durationSecs: asset.lastDuration
            })
          }}
          onFullscreen={() => {
            store.ui.setVideoModalAsset(asset)
            store.analytics.track(NSFW_EVENT.VIDEO_CONTROLS, {
              feedType: store.feed.currentFeedType,
              postId: asset.parentPost.postId,
              controlType: ControlType.FULLSCREEN
            })
          }}
          onReady={(player) => {
            // Missing from types but exists in DOM *shrug*
            onContentLoaded((player as ReactPlayer & { wrapper: HTMLElement }).wrapper)
          }}
        />
      ) : (
        <ImageMediaTile
          media={asset.media}
          tileHeight={height ? `${asset.heightPercentOfWidth}%` : DEF_HEIGHT}
          alt={post.profile?.displayName}
          imageProps={{
            onError: handleAssetError,
            onLoad: (ev) => {
              onContentLoaded(ev.target as HTMLElement)
            }
          }}
        />
      )}
    </Slide>
  )
}

export const AssetSlide = observer(_AssetSlide)
